import { AI_JOB_STATUSES, type AiJobStatus } from '@fos/shared/contracts/ai';
import { type AiJobListItem, COPILOT_ACTIONS, type ListAiJobsInput } from '@fos/shared/contracts/copilot';
import {
  AI_JOB_STATUS_LABELS,
  AI_JOB_STATUS_TONES,
  AI_PROFILE_LABELS,
  aiJobKindLabel,
  CONTEXT_SOURCE_TYPE_LABELS,
} from '@fos/shared/labels';
import { History } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { Link } from 'react-router';
import { formatDateShort, formatDateTimeShort, formatRelativePast, plural } from '../../../lib/format';
import { trpc } from '../../../lib/trpc';
import { Button } from '../../../ui/Button';
import { DateInput } from '../../../ui/DateTimeInput';
import { Sheet } from '../../../ui/Dialog';
import { Field } from '../../../ui/Field';
import { ListRow } from '../../../ui/ListRow';
import { Mono } from '../../../ui/Mono';
import { SectionHeader } from '../../../ui/PageHeader';
import { Section } from '../../../ui/Section';
import { Select } from '../../../ui/Select';
import { EmptyState, ErrorState, ListSkeleton, Skeleton } from '../../../ui/States';
import { Dot, Status } from '../../../ui/Status';
import { formatAiCost, sourceHref } from '../../copilot/copilot-ui';

const ALL = '__wszystkie__';
const KINDS = [...COPILOT_ACTIONS, 'meeting_process'] as const;

/**
 * Historia zadań AI (`ai_jobs`; MVP §12: „historia AIJob i informacja o dostawcy”): rodzaj, opis wejścia (krótki, bez
 * sekretów), dostawca i model, symulacja, status, koszt (z „Widzi stawki i kwoty”), osoba. Filtry: rodzaj, status,
 * okres. Szczegóły: wynik, narzędzia, źródła (typ, tytuł, data), czas trwania, tokeny — bez pełnego wejścia i bez
 * prywatnego toku rozumowania (nie zapisujemy ich). Osoba widzi swoje zadania, właściciel / admin / manager — wszystkie.
 */
export function AiJobsHistory() {
  const [kind, setKind] = useState<string>(ALL);
  const [status, setStatus] = useState<string>(ALL);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);

  const input: ListAiJobsInput = {
    limit: 30,
    ...(kind !== ALL ? { kind } : {}),
    ...(status !== ALL ? { status: status as AiJobStatus } : {}),
    ...(from ? { from } : {}),
    ...(to ? { to } : {}),
  };
  const jobs = trpc.ai.listJobs.useInfiniteQuery(input, {
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const items = jobs.data?.pages.flatMap((p) => p.items) ?? [];
  const filtered = kind !== ALL || status !== ALL || Boolean(from) || Boolean(to);

  let list: ReactNode;
  if (jobs.isPending) list = <ListSkeleton rows={4} />;
  else if (jobs.isError) list = <ErrorState onRetry={() => void jobs.refetch()} retrying={jobs.isFetching} />;
  else if (items.length === 0) {
    list = (
      <EmptyState
        icon={History}
        title={filtered ? 'Brak zadań AI dla tych filtrów' : 'Jeszcze nie było zadań AI'}
        description={
          filtered ? undefined : 'Tu pojawi się każde użycie AI: kto, kiedy, jaki dostawca i ile kosztowało.'
        }
      />
    );
  } else {
    list = (
      <>
        <ul aria-label="Zadania AI">
          {items.map((job) => (
            <li key={job.id}>
              <JobRow job={job} onOpen={() => setOpenId(job.id)} />
            </li>
          ))}
        </ul>
        {jobs.hasNextPage && (
          <div className="mt-4 flex justify-center">
            <Button variant="secondary" onClick={() => void jobs.fetchNextPage()} loading={jobs.isFetchingNextPage}>
              Pokaż starsze
            </Button>
          </div>
        )}
      </>
    );
  }

  return (
    <Section aria-label="Historia AI" className="flex flex-col gap-4">
      <SectionHeader tone="strong" title="Historia zadań AI" />
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Field label="Rodzaj">
          <Select
            value={kind}
            onValueChange={setKind}
            options={[
              { value: ALL, label: 'Wszystkie' },
              ...KINDS.map((k) => ({ value: k, label: aiJobKindLabel(k) })),
            ]}
          />
        </Field>
        <Field label="Status">
          <Select
            value={status}
            onValueChange={setStatus}
            options={[
              { value: ALL, label: 'Wszystkie' },
              ...AI_JOB_STATUSES.map((s) => ({ value: s, label: AI_JOB_STATUS_LABELS[s] })),
            ]}
          />
        </Field>
        <Field label="Od">
          <DateInput value={from} onValueChange={setFrom} />
        </Field>
        <Field label="Do">
          <DateInput value={to} onValueChange={setTo} />
        </Field>
      </div>
      {list}
      <AiJobSheet id={openId} onClose={() => setOpenId(null)} />
    </Section>
  );
}

function JobRow({ job, onOpen }: { job: AiJobListItem; onOpen: () => void }) {
  const meta = [
    formatRelativePast(job.createdAt),
    job.createdBy?.name ?? null,
    job.client?.name ?? null,
    job.simulated ? 'Symulacja' : job.model,
  ]
    .filter(Boolean)
    .join(' · ');
  return (
    <ListRow
      height="l"
      onClick={onOpen}
      leading={<Dot tone={AI_JOB_STATUS_TONES[job.status]} />}
      title={job.inputSummary}
      description={meta}
      trailing={
        <span className="flex items-center gap-3">
          {job.costPln !== undefined && !job.simulated && (
            <Mono className="text-caption text-fg-muted">{formatAiCost(job.costPln)}</Mono>
          )}
          <Status tone={AI_JOB_STATUS_TONES[job.status]}>{AI_JOB_STATUS_LABELS[job.status]}</Status>
        </span>
      }
    />
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-[120px_1fr] gap-3 border-b border-line py-2.5 text-body-s">
      <dt className="text-fg-muted">{label}</dt>
      <dd className="min-w-0 break-words text-fg">{children}</dd>
    </div>
  );
}

function AiJobSheet({ id, onClose }: { id: string | null; onClose: () => void }) {
  const job = trpc.ai.getJob.useQuery({ id: id ?? '' }, { enabled: Boolean(id) });
  const d = job.data;
  return (
    <Sheet
      open={id !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={d ? aiJobKindLabel(d.kind) : 'Zadanie AI'}
      description="Bez pełnego wejścia i bez toku rozumowania — tych danych nie zapisujemy."
    >
      {job.isPending ? (
        <div role="status" aria-label="Wczytywanie" className="flex flex-col gap-3">
          <Skeleton className="h-4 w-2/3" />
          <Skeleton className="h-4 w-1/2" />
          <Skeleton className="h-4 w-3/4" />
        </div>
      ) : job.isError ? (
        <ErrorState compact onRetry={() => void job.refetch()} retrying={job.isFetching} />
      ) : d ? (
        <div className="flex flex-col gap-5">
          <dl>
            <Row label="Status">
              <Status tone={AI_JOB_STATUS_TONES[d.status]}>{AI_JOB_STATUS_LABELS[d.status]}</Status>
            </Row>
            <Row label="Opis">{d.inputSummary}</Row>
            <Row label="Dostawca">{d.simulated ? 'Symulacja — nic nie zostało wysłane do dostawcy' : d.provider}</Row>
            <Row label="Model">
              <Mono>{d.model}</Mono> · {AI_PROFILE_LABELS[d.profile]}
            </Row>
            {d.createdBy && <Row label="Osoba">{d.createdBy.name}</Row>}
            {d.client && (
              <Row label="Klient">
                <Link to={`/klienci/${d.client.id}`} className="text-accent hover:underline">
                  {d.client.name}
                </Link>
              </Row>
            )}
            <Row label="Kiedy">
              {formatDateTimeShort(d.createdAt)}
              {d.durationMs !== null ? ` · ${(d.durationMs / 1000).toFixed(1).replace('.', ',')} s` : ''}
            </Row>
            <Row label="Tokeny">
              <Mono>
                {d.tokensIn} / {d.tokensOut}
              </Mono>
              {d.iterations > 1 ? ` · ${d.iterations} ${plural(d.iterations, ['krok', 'kroki', 'kroków'])}` : ''}
            </Row>
            {d.costPln !== undefined && (
              <Row label="Koszt">
                {formatAiCost(d.costPln)}
                {d.output.priceMissing ? ' · brak ceny modelu w cenniku' : ''}
              </Row>
            )}
            {d.output.toolCalls.length > 0 && <Row label="Narzędzia">{d.output.toolCalls.join(', ')}</Row>}
            {d.contextVersion && (
              <Row label="Kontekst">
                <Mono className="text-caption">{d.contextVersion}</Mono>
              </Row>
            )}
          </dl>
          {d.error && <p className="text-body-s text-danger">{d.error}</p>}
          {d.output.text && (
            <div>
              <p className="mb-1 text-caption font-medium text-fg-muted">Wynik</p>
              <p className="text-body-s whitespace-pre-wrap text-fg-secondary">{d.output.text}</p>
              {d.output.truncated && (
                <p className="mt-1 text-caption text-fg-muted">Odpowiedź ucięta limitem długości.</p>
              )}
            </div>
          )}
          {d.output.sources.length > 0 && (
            <div>
              <p className="mb-1 text-caption font-medium text-fg-muted">Źródła</p>
              <ul className="flex flex-col gap-1 text-body-s">
                {d.output.sources.map((s) => {
                  const href = sourceHref(s);
                  const label = `${CONTEXT_SOURCE_TYPE_LABELS[s.type]}: ${s.title.replace(/^[^:]+:\s*/, '')}`;
                  return (
                    <li key={`${s.type}:${s.id ?? s.title}`} className="flex min-w-0 items-baseline gap-2">
                      {href ? (
                        <Link to={href} className="min-w-0 truncate text-fg hover:underline">
                          {label}
                        </Link>
                      ) : (
                        <span className="min-w-0 truncate text-fg">{label}</span>
                      )}
                      {s.date && <span className="shrink-0 text-caption text-fg-muted">{formatDateShort(s.date)}</span>}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      ) : null}
    </Sheet>
  );
}
